import { TouchAdapter } from './TouchAdapter.js';
import { MouseAdapter } from './MouseAdapter.js';
import { WheelAdapter } from './WheelAdapter.js';

/**
 * GestureController - Unified gesture handling for swipe navigation
 *
 * Coordinates touch, mouse, and trackpad input through adapters.
 * Provides direction locking, threshold detection, and nested scroll handling.
 *
 * @example
 * const controller = new GestureController(element, {
 *   threshold: 100,
 *   onGestureStart: () => {},
 *   onGestureMove: ({ deltaX, deltaY, direction, progress }) => {},
 *   onGestureEnd: ({ deltaX, deltaY, direction, thresholdMet, action }) => {},
 *   shouldLockDirection: (direction) => true,
 *   isNestedScrollable: (target) => target.closest('.scrollable'),
 * });
 */
export class GestureController {
  constructor(element, options = {}) {
    this.element = element;

    // Configuration
    this.threshold = options.threshold ?? 100;
    this.directionLockThreshold = options.directionLockThreshold ?? 10;

    // Callbacks
    this.onGestureStart = options.onGestureStart ?? (() => {});
    this.onGestureMove = options.onGestureMove ?? (() => {});
    this.onGestureEnd = options.onGestureEnd ?? (() => {});

    // Direction control - consumer can restrict to vertical only, etc.
    this.shouldLockDirection = options.shouldLockDirection ?? (() => true);

    // Nested scroll detection - consumer provides check function
    this.isNestedScrollable = options.isNestedScrollable ?? (() => null);

    // Internal state
    this.active = false;
    this.locked = false;
    this.startX = 0;
    this.startY = 0;
    this.deltaX = 0;
    this.deltaY = 0;
    this.direction = null; // 'h' | 'v' | null

    // Nested scroll state
    this.nestedScrollElement = null;
    this.nestedScrollStartX = 0;
    this.nestedScrollStartLeft = 0;
    this.isNestedScrolling = false;

    // Initialize adapters
    this.adapters = [
      new TouchAdapter(this),
      new MouseAdapter(this),
      new WheelAdapter(this, {
        sensitivity: options.wheelSensitivity ?? 1.5,
        maxDelta: options.wheelMaxDelta ?? 600,
      }),
    ];
  }

  /**
   * Check if gestures should be blocked (e.g., modal open)
   */
  isLocked() {
    return this.locked;
  }

  /**
   * Lock/unlock gesture recognition
   */
  setLocked(locked) {
    this.locked = locked;
    if (locked && this.active) {
      this.cancel();
    }
  }

  /**
   * Called by adapters when gesture starts
   */
  handleStart(x, y, event) {
    if (this.locked) return;

    // Check for nested scrollable element
    if (event?.target) {
      const scrollable = this.isNestedScrollable(event.target);
      if (scrollable) {
        const canScrollX = scrollable.scrollWidth > scrollable.clientWidth;
        const canScrollY = scrollable.scrollHeight > scrollable.clientHeight;

        if (canScrollX || canScrollY) {
          this.nestedScrollElement = scrollable;
          this.nestedScrollStartX = x;
          this.nestedScrollStartLeft = scrollable.scrollLeft;
          this.nestedScrollStartTop = scrollable.scrollTop;
          this.nestedScrollCanX = canScrollX;
          this.nestedScrollCanY = canScrollY;
        }
      }
    }

    this.active = true;
    this.startX = x;
    this.startY = y;
    this.deltaX = 0;
    this.deltaY = 0;
    this.direction = null;
    this.isNestedScrolling = false;

    this.onGestureStart({ x, y, event });
  }

  /**
   * Called by adapters during gesture
   * @param {boolean} isWheel - If true, x/y are already computed deltas
   */
  handleMove(x, y, event, isWheel = false) {
    if (!this.active || this.locked) return;

    // For wheel, x/y are already deltas; for touch/mouse, compute from start
    if (isWheel) {
      this.deltaX = x;
      this.deltaY = y;
    } else {
      this.deltaX = x - this.startX;
      this.deltaY = y - this.startY;
    }

    // Handle nested scrolling (touch/mouse only)
    if (!isWheel && this.nestedScrollElement) {
      const result = this.handleNestedScroll(x, y, event);
      if (result === 'captured') {
        return; // Nested element is handling this
      }
    }

    // Lock direction after threshold
    if (!this.direction) {
      const absX = Math.abs(this.deltaX);
      const absY = Math.abs(this.deltaY);

      if (absX > this.directionLockThreshold || absY > this.directionLockThreshold) {
        const preferredDirection = absX > absY ? 'h' : 'v';

        // Allow consumer to override (e.g., force vertical on landing page)
        if (this.shouldLockDirection(preferredDirection)) {
          this.direction = preferredDirection;
        } else {
          // Consumer rejected horizontal, force vertical
          this.direction = 'v';
        }
      }
    }

    // Compute progress toward threshold
    const progress = this.direction === 'h'
      ? this.deltaX / this.threshold
      : this.deltaY / this.threshold;

    this.onGestureMove({
      deltaX: this.deltaX,
      deltaY: this.deltaY,
      direction: this.direction,
      progress,
      event,
    });
  }

  /**
   * Handle scrolling within a nested scrollable element
   * Returns 'captured' if nested element is handling the scroll
   */
  handleNestedScroll(x, y, event) {
    const el = this.nestedScrollElement;
    const dx = x - this.nestedScrollStartX;
    const dy = y - this.startY;

    // Detect if user is scrolling within nested element
    if (!this.isNestedScrolling && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
      const atLeftEdge = el.scrollLeft <= 0;
      const atRightEdge = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1;
      const atTopEdge = el.scrollTop <= 0;
      const atBottomEdge = el.scrollTop >= el.scrollHeight - el.clientHeight - 1;

      // Horizontal scroll: capture if not at edges or scrolling into content
      const captureX = this.nestedScrollCanX && Math.abs(dx) > Math.abs(dy) && (
        (!atLeftEdge && !atRightEdge) ||
        (atLeftEdge && dx < 0) ||
        (atRightEdge && dx > 0)
      );

      // Vertical scroll: capture if not at edges or scrolling into content
      const captureY = this.nestedScrollCanY && Math.abs(dy) > Math.abs(dx) && (
        (!atTopEdge && !atBottomEdge) ||
        (atTopEdge && dy < 0) ||
        (atBottomEdge && dy > 0)
      );

      if (captureX || captureY) {
        this.isNestedScrolling = true;
      }
    }

    return this.isNestedScrolling ? 'captured' : null;
  }

  /**
   * Lock direction externally (used by WheelAdapter)
   */
  lockDirection(direction) {
    if (!this.direction && this.shouldLockDirection(direction)) {
      this.direction = direction;
    } else if (!this.direction) {
      this.direction = 'v';
    }
  }

  /**
   * Called by adapters when gesture ends
   */
  handleEnd(event, wheelData = null) {
    if (!this.active) return;

    // If nested scrolling, just reset
    if (this.isNestedScrolling) {
      this.reset();
      return;
    }

    // Use wheel data if provided (has accumulated deltas)
    const finalDeltaX = wheelData?.deltaX ?? this.deltaX;
    const finalDeltaY = wheelData?.deltaY ?? this.deltaY;
    const finalDirection = wheelData?.direction ?? this.direction;

    // Determine if threshold was met and which action
    const thresholdMetX = Math.abs(finalDeltaX) > this.threshold;
    const thresholdMetY = Math.abs(finalDeltaY) > this.threshold;

    let action = null;
    if (finalDirection === 'h' && thresholdMetX) {
      action = finalDeltaX > 0 ? 'swipe-right' : 'swipe-left';
    } else if (finalDirection === 'v' && thresholdMetY) {
      action = finalDeltaY > 0 ? 'swipe-down' : 'swipe-up';
    }

    this.onGestureEnd({
      deltaX: finalDeltaX,
      deltaY: finalDeltaY,
      direction: finalDirection,
      thresholdMet: action !== null,
      action,
      event,
    });

    this.reset();
  }

  /**
   * Cancel current gesture without triggering end callback
   */
  cancel() {
    this.reset();
  }

  /**
   * Reset internal state
   */
  reset() {
    this.active = false;
    this.startX = 0;
    this.startY = 0;
    this.deltaX = 0;
    this.deltaY = 0;
    this.direction = null;
    this.nestedScrollElement = null;
    this.isNestedScrolling = false;
  }

  /**
   * Get current state
   */
  getState() {
    return {
      active: this.active,
      deltaX: this.deltaX,
      deltaY: this.deltaY,
      direction: this.direction,
      isHorizontal: this.direction === 'h',
      isVertical: this.direction === 'v',
    };
  }

  /**
   * Clean up event listeners
   */
  destroy() {
    this.adapters.forEach(adapter => adapter.destroy());
    this.adapters = [];
  }
}
